import dynamic from 'next/dynamic'
import { useEffect, useRef, useState } from 'react'
import { BasicStyledComponent } from 'shared-definitions/types'
import { AuthContext } from 'shared-components/contexts/AuthContext'
import { PostContext } from 'shared-components/contexts/PostContext'
import { ResponsiveContext } from 'shared-components/contexts/ResponsiveContext'
import ConversationSheet from 'shared-components/layout/ConversationSheet'
import ErrorBoundary from 'shared-components/service/ErrorBoundary'
import { CONVERSATION_TITLE_ID, ConversationContext } from './ConversationContext'
import ConversationEndBlock from './ConversationEndBlock'
import ConversationStickyBar from './ConversationStickyBar'
import { markConversationRead } from './conversationLastRead'
import { useTrackConversationDwell } from './useTrackConversationDwell'
import { useTrackConversationOpened } from './useTrackConversationOpened'

// The thread is client-only, as the inline widget always was (`ui/Comments.tsx`).
const ConversationPanel = dynamic(() => import('./ConversationPanel'), { ssr: false })

/** A URL fragment that asks for the conversation: the section anchor or one comment. */
export function isConversationHash(hash: string, onLoad: boolean): boolean {
  return hash.startsWith('#conv-comment-') || (onLoad && hash === '#comments-section')
}

/** The comment a `#conv-comment-{id}` fragment links to (the widget's own pattern); else null. */
export function conversationHashCommentId(hash: string): number | null {
  const match = /^#conv-comment-(\d+)$/.exec(hash)
  return match ? Number(match[1]) : null
}

/**
 * The history entry's state with the hash gone too: the Next router keeps the URL it routed to in
 * `state.as`, and a Back to this entry routes (and rewrites the address) from it — with the hash
 * still there, the panel would reopen on the old link. Anything else in the state is kept.
 */
export function historyStateWithout(state: unknown, url: string): unknown {
  if (state && typeof state === 'object' && typeof (state as { as?: unknown }).as === 'string') {
    return { ...state, as: url }
  }
  return state
}

interface HostProps {
  containerId: string
  className?: string
  /** The page has another fixed bottom surface (SoundGuys' LabCompare drawer): no mobile bar. */
  hideBar: boolean
}

const Host: React.VFC<HostProps> = ({ containerId, className, hideBar }) => {
  const { isOpen, request, open, close, registerHost } = ConversationContext.useContainer()
  const { ready, lg } = ResponsiveContext.useContainer()
  /** The comment the open panel locates (a `#conv-comment-{id}` link); null = none. */
  const [deepLinkId, setDeepLinkId] = useState<number | null>(null)

  useEffect(() => registerHost(containerId), [containerId, registerHost])
  useTrackConversationOpened()
  useTrackConversationDwell()

  // Every open the reader asked for (byline, rail, bar, end block) is a step toward posting, and
  // signed out the panel's composer opens the sign-in: start the Google script now
  // (`prewarmGoogle`, a no-op when signed in). Not a deep link — a shared link opened this page.
  const prewarmGoogle = AuthContext.useOptionalContainer()?.prewarmGoogle
  const openSeq = request && request.source !== 'deeplink' ? request.seq : null
  useEffect(() => {
    if (openSeq !== null) {
      prewarmGoogle?.()
    }
  }, [openSeq, prewarmGoogle])

  // Deep links (decision 6): on load `#comments-section` or `#conv-comment-{id}`; while on the
  // page, a notification's `#conv-comment-{id}`. The widget's locate logic runs in the panel.
  // The hash is read HERE, once, and then cleared (`replaceState`: no `hashchange`, no scroll,
  // the router's history state kept minus the hash — `historyStateWithout`). The panel's widget
  // gets the id as `frame.deepLinkId` instead of reading the hash on mount — it mounts later
  // (lazy chunk) and on every open, so a hash left in the URL re-located and re-flashed the
  // comment on each later open. The id lives until the panel closes. A link followed while the
  // panel is already open does not re-locate (as before).
  useEffect(() => {
    const take = (onLoad: boolean): void => {
      const { hash, pathname, search } = window.location
      if (!isConversationHash(hash, onLoad)) {
        return
      }
      setDeepLinkId(conversationHashCommentId(hash))
      window.history.replaceState(
        historyStateWithout(window.history.state, pathname + search),
        '',
        pathname + search
      )
      open('deeplink')
    }
    take(true)
    const onHash = (): void => take(false)
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [open])

  // Closed: the deep link is spent — the next open lands like any other. Opened and closed: the
  // reader's last read moves on ("N new since you last read" in the mobile top bar).
  const wasOpen = useRef(false)
  useEffect(() => {
    if (wasOpen.current && !isOpen) {
      setDeepLinkId(null)
    }
    if (isOpen || wasOpen.current) {
      markConversationRead(containerId)
    }
    wasOpen.current = isOpen
  }, [isOpen, containerId])

  const variant = lg ? 'drawer' : 'sheet'
  return (
    <>
      <div id="comments-section" data-container-type="content" className={className}>
        <ConversationEndBlock containerId={containerId} />
      </div>
      <ConversationStickyBar hidden={hideBar} />
      {ready && (
        <ConversationSheet
          open={isOpen}
          variant={variant}
          onClose={close}
          labelledBy={CONVERSATION_TITLE_ID}
        >
          <ConversationPanel
            containerId={containerId}
            compact={variant === 'sheet'}
            autoFocusComposer={request?.focusComposer ?? false}
            focusSeq={request?.seq ?? 0}
            deepLinkId={deepLinkId}
            onClose={close}
          />
        </ConversationSheet>
      )}
    </>
  )
}

/**
 * The article's conversation host (comments always in reach, decisions 1 and 5): mounted once
 * by each article template where the inline thread used to be. It registers the page's
 * commentHub container in `ConversationContext`, renders the end-of-article block in the
 * thread's old place (`#comments-section`, so the byline fallback and old in-page links still
 * resolve; the block renders from this host's `containerId` prop, so it is in the server HTML),
 * and — while the context says open — the panel in a `ConversationSheet`: the right drawer from lg, the bottom sheet below (`ResponsiveContext`; nothing until it is ready).
 * The mobile top bar is mounted here too (fixed, so its DOM place is irrelevant) — one per
 * article page; `hideBar` yields to a page's other fixed surface. Each open and close moves the
 * reader's last read on (`conversationLastRead.ts`). Every open is tracked
 * once (`conversation-opened`). The comment policy line is the panel's own, not this host's.
 *
 * Nothing on a sponsored post or a page without a container, like `ui/Comments.tsx`.
 */
interface ArticleConversationProps extends BasicStyledComponent {
  /** True on pages that mount another fixed bottom surface (SoundGuys' LabCompare drawer). */
  hideBar?: boolean
}

const ArticleConversation: React.VFC<ArticleConversationProps> = ({ className, hideBar }) => {
  const { engagementContainerId: id, sponsored } = PostContext.useContainer()
  const containerId = sponsored || !id ? null : id

  if (!containerId) {
    return null
  }
  return (
    <ErrorBoundary>
      <Host containerId={containerId} className={className} hideBar={hideBar ?? false} />
    </ErrorBoundary>
  )
}

export default ArticleConversation
